import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
  CheckCircle,
  Store,
  Building2,
  Factory,
  ShieldCheck,
  Server,
  CreditCard,
  Package,
} from "lucide-react";

/* ---------------- TYPES ---------------- */

interface Plan {
  name: string;
  price: string;
  gstNote: string;
  description: string;
  icon: React.ElementType;
  features: string[];
  addons?: string[];
  terminals?: string;
  cta: string;
  highlight?: boolean;
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

/* ---------------- COMPONENT ---------------- */

const PricingPage: React.FC = () => {
  const plans: Plan[] = [
    {
      name: "Starter POS",
      price: "₹499 / month",
      gstNote: "+ GST",
      description: "Single-store retailers & kirana shops",
      icon: Store,
      terminals: "1 Terminal Included",
      features: [
        "POS Billing & Invoicing",
        "Barcode Scanning",
        "Offline Billing Mode",
        "Basic Inventory Management",
        "Daily Sales Reports",
      ],
      addons: ["Extra Terminal: Contact Sales", "Delivery Integration: Contact Sales"],
      cta: "Get Started",
    },
    {
      name: "Growth Retail",
      price: "₹699 / month",
      gstNote: "+ GST",
      description: "Supermarkets & growing retail chains",
      icon: Building2,
      highlight: true,
      terminals: "Up to 5 Terminals Included",
      features: [
        "Everything in Starter",
        "Multi-Store Management",
        "Online Ordering Integration",
        "Customer Loyalty & Offers",
        "Advanced Reports & Analytics",
      ],
      addons: ["Extra Terminal: Contact Sales", "ERP Integration: Contact Sales"],
      cta: "Request Demo",
    },
    {
      name: "Enterprise POS",
      price: "Custom Pricing",
      gstNote: "GST as applicable",
      description: "Large enterprises & franchises",
      icon: Factory,
      terminals: "Unlimited Terminals",
      features: [
        "Everything in Growth",
        "Unlimited Stores & Terminals",
        "On-Premise / Private Cloud",
        "ERP & Accounting Integration",
        "Custom Workflows & APIs",
        "Dedicated Account Manager",
      ],
      addons: ["Extra Terminals: Contact Sales", "Custom Add-ons: Contact Sales"],
      cta: "Talk to Sales",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* NAVBAR */}
            <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur">
              <div className="max-w-7xl mx-auto px-1 flex h-16 items-center justify-between">
                <img
                  src="/assets/logo/rnb-logo.png"
                  alt="RetailNextBox"
                  className="h-60 w-80"
                />
                <div className="flex gap-3">
                  {/* <Link to="/pricing">
                    <Button variant="outline">Pricing</Button>
                  </Link> */}
                  { <Link to="/requestdemo">
                    <Button className="bg-gradient-primary">Request Demo</Button>
                  </Link> }
                </div>
              </div>
            </nav>
      {/* HEADER */}
      <section className="py-20 text-center">
        <Badge className="bg-primary/10 text-primary">Pricing</Badge>
        <h1 className="text-4xl font-bold mt-4">Enterprise-Ready POS Pricing</h1>
        <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
          Simple, transparent pricing for Indian retailers. Scales from kirana shops to enterprise chains. All prices exclusive of GST.
        </p>
      </section>

      {/* PRICING CARDS */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className={`relative h-full ${plan.highlight ? "shadow-xl scale-[1.02] border-primary" : ""}`}>
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="p-8 space-y-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                      <plan.icon className="w-6 h-6" />
                    </div>
                    <h2 className="text-xl font-semibold">{plan.name}</h2>
                  </div>

                  <div>
                    <p className="text-3xl font-bold">{plan.price}</p>
                    <p className="text-sm text-muted-foreground">{plan.gstNote}</p>
                    {plan.terminals && <p className="text-sm text-muted-foreground mt-1">{plan.terminals}</p>}
                  </div>

                  <p className="text-muted-foreground">{plan.description}</p>

                  <ul className="space-y-3 pt-2">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-primary" /> {feature}
                      </li>
                    ))}
                  </ul>

                  {plan.addons && (
                    <div className="pt-4">
                      <h3 className="font-semibold text-sm text-muted-foreground mb-2">Add-ons</h3>
                      <ul className="space-y-2">
                        {plan.addons.map((addon, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-primary">
                            <Package className="w-4 h-4" /> {addon}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="pt-4">
                    <Link to="/requestdemo">
                      <Button
                        className={`w-full ${plan.highlight ? "bg-gradient-primary" : ""}`}
                        variant={plan.highlight ? "default" : "outline"}
                      >
                        {plan.cta}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* GST & TRUST SECTION */}
      <section className="py-16 bg-muted/40">
        <div className="max-w-5xl mx-auto px-6 text-center space-y-6">
          <ShieldCheck className="w-10 h-10 mx-auto text-primary" />
          <h3 className="text-2xl font-semibold">GST-Ready & Compliant</h3>
          <p className="text-muted-foreground">
            Retailnextbox pricing follows Indian GST regulations. All invoices, daily sales reports, and taxation are fully GST-compliant.
          </p>
        </div>
      </section>

      {/* CLOUD vs ON-PREM */}
      <section className="py-20 text-center">
        <h2 className="text-3xl font-bold">Deployment Options</h2>
        <p className="mt-3 text-muted-foreground">
          Flexible options to suit your business size and IT setup.
        </p>

        <div className="max-w-5xl mx-auto mt-10 grid md:grid-cols-2 gap-8">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Server className="w-6 h-6 text-primary" />
              <h3 className="font-semibold text-lg">Cloud</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Managed cloud deployment with 99.9% uptime. Access your POS & reports anywhere.
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" /> Automatic Backups
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" /> No IT Setup Required
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" /> Instant Updates
              </li>
            </ul>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <Factory className="w-6 h-6 text-primary" />
              <h3 className="font-semibold text-lg">On-Premise / Private Cloud</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              Full control over your infrastructure with private cloud or on-premise installation.
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" /> Custom Integrations
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" /> Dedicated Support & SLA
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" /> On-Prem Security
              </li>
            </ul>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-center">
        <h2 className="text-3xl font-bold">Custom Plans & Add-ons</h2>
        <p className="mt-3 text-muted-foreground">
          Large retail chains, franchises, and enterprises — let’s create a plan that fits your business perfectly.
        </p>
        <div className="mt-6 flex justify-center gap-4">
          <Link to="/requestdemo">
            <Button size="lg" className="bg-gradient-primary">
              Contact Sales
            </Button>
          </Link>
          <Link to="/">
            <Button size="lg" variant="outline">
              Back to Home
            </Button>
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t py-8 text-center text-sm text-muted-foreground">
        © 2025 RetailNextBox. Prices exclusive of applicable GST.
      </footer>
    </div>
  );
};

export default PricingPage;
